import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { api } from "../../lib/api";
import { ASSETS, CONTACTS } from "../../lib/data";
import { Input } from "../ui/input";

export const Footer = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.status === "already_subscribed" ? t.news.already : t.news.success);
      setEmail("");
    } catch {
      toast.error(t.lead.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative z-10 border-t border-white/10 bg-[#141414]" data-testid="footer">
      {/* Newsletter */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center border-b border-white/10">
        <div>
          <h3 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl">{t.news.title}</h3>
          <p className="text-white/50 mt-3 max-w-sm">{t.news.sub}</p>
        </div>
        <form onSubmit={subscribe} className="flex items-center gap-3" data-testid="newsletter-form">
          <Input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.news.placeholder}
            data-testid="newsletter-email"
            className="bg-charcoal border-white/15 h-12 flex-1"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="newsletter-submit"
            className="inline-flex items-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full px-6 py-3.5 transition-colors duration-300 disabled:opacity-60 whitespace-nowrap"
          >
            {t.news.cta} <ArrowRight size={16} />
          </button>
        </form>
      </div>

      {/* Contact numbers */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-b border-white/10" data-testid="footer-contacts">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/40">
          <Phone size={14} className="text-lime2" /> {t.footer.contact}
        </span>
        {CONTACTS.map((num) => (
          <a
            key={num}
            href={`tel:${num.replace(/\s+/g, "")}`}
            data-testid={`footer-contact-${num.replace(/\D/g, "")}`}
            className="text-sm font-medium text-white/75 hover:text-lime2 transition-colors"
          >
            {num}
          </a>
        ))}
      </div>

      {/* Bottom */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="Gesung" className="h-9 w-9 rounded-md object-cover" />
          <div>
            <p className="font-display font-extrabold tracking-tight">GESUNG GREEN POWER</p>
            <p className="text-lime2 text-xs">{t.footer.tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm text-white/40">
          <span>© {new Date().getFullYear()} Gesung. {t.footer.rights}</span>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-lime2 transition-colors">
            {t.footer.admin}
          </Link>
        </div>
      </div>
    </footer>
  );
};
